/**
 * Campanhas estratégicas — dados e cálculos portados do protótipo
 * (`prototipo/Mandato360-prototipo-identidade_3.html`, bloco em torno das
 * linhas 1599–1662; cadeia de `RENDER.campanhas` documentada em
 * `task-5.1-report.md`).
 *
 * `ESCALAS` e `BANCO_PERGUNTAS` são copiados literalmente (proto:1599-1600).
 * `sugerirPerguntas`, `simularResultados`, `cpLeitura`, `cpFiltrar` e
 * `cpResponder` são traduções linha a linha das funções homônimas do
 * protótipo (proto:1601-1614, 1644-1651), sem reinterpretar a fórmula —
 * inclusive mantendo o `+(p.e==='likert'?0:0)` sem efeito (proto:1607).
 *
 * Onde o protótipo usava número de demonstração e existe dado real, esta porta
 * usa o dado real: a taxa de resposta sai de `respostas/enviadas` das campanhas
 * do mandato (`taxaRespostaHistorica`) e a divergência entre bairros sai da
 * `distribuicaoPorBairro` já calculada (`divergenciaPorBairro`). Quando o dado
 * não existe, a função devolve `null` e a tela DECLARA A LACUNA nomeando a
 * causa — nunca estima.
 *
 * Honestidade de dado: o próprio protótipo revela, no seu próprio texto, que
 * o resultado é simulado ("resultados simulados para o protótipo",
 * proto:1628) e que a leitura real chegaria pelo webhook de respostas. Esta
 * porta não fabrica uma fonte de verdade nova: `simularResultados` continua
 * sendo uma demonstração explicitamente rotulada como tal na UI
 * (`WizardCampanha.tsx`), calculada em cima do público real (contatos com
 * opt-in ativo), nunca sobre números inventados de destinatários.
 */

export type TipoEscala = 'likert' | 'sn' | 'prio' | 'nota' | 'aberta';

export const ESCALAS: Record<TipoEscala, string[]> = {
  likert: [
    'Concordo totalmente',
    'Concordo em parte',
    'Neutro',
    'Discordo em parte',
    'Discordo totalmente',
  ],
  sn: ['Sim', 'Não', 'Não sei'],
  prio: ['Saúde', 'Educação/creche', 'Ruas e iluminação', 'Saneamento', 'Segurança', 'Emprego'],
  nota: ['1', '2', '3', '4', '5'],
  aberta: ['texto livre'],
};

export interface PerguntaBase {
  q: string;
  e: TipoEscala;
  opts?: string[];
}

export interface Pergunta extends PerguntaBase {
  id: string;
  on: boolean;
}

export type TemaPerguntas = 'seguranca' | 'saude' | 'geral' | 'creche';

export const BANCO_PERGUNTAS: Record<TemaPerguntas, PerguntaBase[]> = {
  seguranca: [
    { q: 'O bairro está mais seguro do que há 2 anos?', e: 'likert' },
    {
      q: 'Você concorda com a presença de forças de fora (Exército/Força Nacional) no bairro?',
      e: 'likert',
    },
    { q: 'Você concorda em tratar facções como organizações terroristas?', e: 'likert' },
    {
      q: 'Prefere mais policiamento ou mais projetos sociais para a juventude?',
      e: 'sn',
      opts: ['Policiamento', 'Projetos sociais', 'Os dois'],
    },
    { q: 'Você se sente seguro(a) para andar à noite na sua rua?', e: 'likert' },
    { q: 'Qual o maior problema de segurança na sua rua hoje?', e: 'aberta' },
  ],
  saude: [
    { q: 'Conseguiu consulta na UBS nos últimos 3 meses?', e: 'sn' },
    {
      q: 'Quantos dias esperou?',
      e: 'nota',
      opts: ['até 7', '8–15', '16–30', '31–60', 'mais de 60'],
    },
    { q: 'A UBS deveria abrir aos sábados?', e: 'likert' },
    { q: 'Concorda que telemedicina resolve parte das consultas?', e: 'likert' },
    { q: 'Qual especialidade mais falta?', e: 'aberta' },
  ],
  geral: [
    { q: 'Qual a maior necessidade do seu bairro hoje?', e: 'prio' },
    { q: 'A vereadora tem presença no seu bairro?', e: 'likert' },
    { q: 'Você sabe de alguma ação dela no bairro?', e: 'sn' },
    { q: 'Nota para o atendimento do gabinete', e: 'nota' },
    { q: 'O que você mudaria primeiro?', e: 'aberta' },
  ],
  creche: [
    { q: 'Há criança de 0–3 anos em casa sem vaga em creche?', e: 'sn' },
    { q: 'Creche em tempo integral mudaria a renda da família?', e: 'likert' },
    { q: 'Aceitaria creche conveniada (privada com vaga pública)?', e: 'likert' },
    {
      q: 'Prefere creche nova ou ampliar a existente?',
      e: 'sn',
      opts: ['Nova', 'Ampliar', 'Tanto faz'],
    },
  ],
};

/** proto:1601 — detecta o tema a partir do texto do objetivo. */
export function detectarTema(objetivo: string): TemaPerguntas {
  const t = objetivo.toLowerCase();
  if (/segur|faccão|facção|polic|terror|violên/.test(t)) return 'seguranca';
  if (/saúde|ubs|consulta|médic/.test(t)) return 'saude';
  if (/creche|educa|escola/.test(t)) return 'creche';
  return 'geral';
}

/** proto:1601 — `sugerirPerguntas`, sem o efeito colateral em `STATE.cp`. */
export function sugerirPerguntas(objetivo: string): { tema: TemaPerguntas; perguntas: Pergunta[] } {
  const tema = detectarTema(objetivo);
  const base: PerguntaBase[] = [...BANCO_PERGUNTAS[tema]];
  if (tema !== 'geral') base.unshift(BANCO_PERGUNTAS.geral[0]!);
  if (!base.some((p) => p.e === 'aberta')) {
    base.push({ q: 'O que mais você quer dizer sobre isso?', e: 'aberta' });
  }
  return {
    tema,
    perguntas: base.map((p, i) => ({ id: `P${i + 1}`, ...p, on: true })),
  };
}

export type ModoPublico = 'bairro' | 'mun' | 'uf' | 'todos' | 'amostra';

export interface PublicoDef {
  modo: ModoPublico;
  bairro?: string;
  municipio?: string;
  uf?: string;
  tag?: string;
  amostra: number;
}

export const PUBLICO_DEF_PADRAO: PublicoDef = {
  modo: 'bairro',
  amostra: 200,
};

/**
 * Campos que cada modo de público precisa trazer — espelho da invariante que o
 * servidor aplica em `CampanhasService.exigirRecorteCoerente`.
 *
 * Aqui é conveniência: antecipa a recusa com uma mensagem que aponta o campo em
 * falta, em vez de deixar a pessoa clicar em "aprovar e enviar" para receber um
 * 400. A guarda que vale é a do servidor.
 */
const CAMPOS_EXIGIDOS_POR_MODO: Record<ModoPublico, (keyof PublicoDef)[]> = {
  bairro: ['bairro'],
  mun: ['municipio', 'uf'],
  uf: ['uf'],
  amostra: ['amostra'],
  todos: [],
};

const ROTULO_CAMPO: Record<string, string> = {
  bairro: 'o bairro',
  municipio: 'o município',
  uf: 'a UF',
  amostra: 'o tamanho da amostra',
};

/**
 * Devolve o motivo pelo qual o recorte não pode virar disparo, ou `null` se ele
 * está coerente. Campo ausente NÃO restringe menos: amplia — por isso é erro, e
 * não um aviso.
 */
export function motivoPublicoIncoerente(publico: PublicoDef): string | null {
  if (publico.modo === 'amostra' && !(Number.isInteger(publico.amostra) && publico.amostra >= 1)) {
    return 'Informe um tamanho de amostra inteiro e maior que zero: valor inválido dispararia para toda a base.';
  }
  const faltando = (CAMPOS_EXIGIDOS_POR_MODO[publico.modo] ?? [])
    .filter((c) => c !== 'amostra' && String(publico[c] ?? '').trim() === '')
    .map((c) => ROTULO_CAMPO[c as string] ?? String(c));
  if (faltando.length) {
    return `Preencha ${faltando.join(' e ')} antes de enviar: sem esse dado o disparo iria para toda a base.`;
  }
  return null;
}

/**
 * Descreve em uma linha o recorte realmente gravado na campanha.
 *
 * A tela mostrava só `publico_def.bairro` e caía em "todos os territórios"
 * sempre que o recorte era outro — município, UF ou amostra apareciam como se
 * a campanha tivesse ido para o mandato inteiro. Subestimar o recorte numa tela
 * de disparo é o tipo de engano que faz alguém reenviar "porque não pegou".
 */
export function descreverPublico(def: Record<string, any> | null | undefined): string {
  if (!def) return 'não informado';
  const partes: string[] = [];
  if (def.bairro) partes.push(`bairro ${def.bairro}`);
  if (def.municipio) partes.push(`município ${def.municipio}${def.uf ? `/${def.uf}` : ''}`);
  else if (def.uf) partes.push(`UF ${def.uf}`);
  if (Number(def.amostra) > 0) partes.push(`amostra de ${Number(def.amostra)}`);
  if (def.tag) partes.push(`tag ${def.tag}`);
  if (!partes.length) return def.modo === 'todos' ? 'todos os contatos com opt-in' : 'todos os territórios';
  return partes.join(' · ');
}

/** Contato mínimo necessário para simular a distribuição por bairro. */
export interface ContatoParaSimulacao {
  bairro?: string;
  municipio?: string;
}

export interface RespostaPergunta {
  pergunta: Pergunta;
  opcoes: string[];
  /** Distribuição de respostas por bairro, na mesma ordem de `opcoes`. */
  distribuicaoPorBairro: Record<string, number[]>;
  totais: number[];
  respostas: number;
}

export interface SegmentoCruzado {
  pct: number;
  nome?: string;
  leitura?: string;
}

export interface ResultadoCampanha {
  id: string;
  convidados: number;
  respostas: number;
  taxaResposta: number;
  bairros: string[];
  /** Município de cada bairro coberto — vem do cadastro do contato, não é inferido. */
  municipioPorBairro: Record<string, string>;
  porPergunta: RespostaPergunta[];
  segmentos: SegmentoCruzado[];
  data: string;
  /**
   * `true` quando a distribuição de respostas veio de `simularResultados` e
   * não de resposta coletada. Quem exibe um número derivado daqui é obrigado
   * a rotulá-lo como simulado.
   */
  simulado: boolean;
  /** Taxa usada para derivar `respostas` a partir de `convidados`. */
  taxaUsada: { valor: number; origem: 'historico' | 'referencia_nao_calculada' };
}

/**
 * Constante do protótipo (proto:1604, `n*0.38`). NÃO é medida sobre campanha
 * nenhuma: é um valor de referência do desenho original. Só deve ser usada
 * quando o mandato ainda não tem campanha com envio registrado — e, quando
 * for usada, a tela precisa dizer que não foi calculada (ver `taxaUsada`).
 */
export const TAXA_RESPOSTA_REFERENCIA = 0.38;

/**
 * Taxa de resposta REAL do mandato: respostas / enviadas somadas sobre as
 * campanhas que já tiveram envio. Devolve `null` quando não há base — e aí
 * quem chama declara a lacuna, em vez de cair num número inventado.
 */
export function taxaRespostaHistorica(
  campanhas: { enviadas?: number | null; respostas?: number | null }[],
): { taxa: number; enviadas: number; respostas: number; campanhas: number } | null {
  const comEnvio = campanhas.filter((c) => (c.enviadas ?? 0) > 0);
  const enviadas = comEnvio.reduce((a, c) => a + (c.enviadas ?? 0), 0);
  if (!enviadas) return null;
  const respostas = comEnvio.reduce((a, c) => a + (c.respostas ?? 0), 0);
  return { taxa: respostas / enviadas, enviadas, respostas, campanhas: comEnvio.length };
}

/** Gerador congruente linear — proto:1605, usado só para distribuir a demonstração. */
function criarGerador(seedInicial: number) {
  let s = seedInicial;
  return () => {
    s = (s * 9301 + 49297) % 233280;
    return s / 233280;
  };
}

/**
 * proto:1604 `simularResultados` — traduzido linha a linha. `contatos` é o
 * público real (opt-in, já filtrado pelo modo escolhido); `perguntasAtivas`
 * são só as perguntas ligadas (`p.on`). `seed` deixa o teste determinístico.
 *
 * Divergência do brief: a assinatura do protótipo não recebe parâmetros (lê
 * `STATE.cp` global) e não devolve nada (muta `STATE.cp.resultados`, um
 * objeto único — não um array, como o brief descrevia). Aqui a função é
 * pura e devolve um único `ResultadoCampanha`.
 */
export function simularResultados(
  contatos: ContatoParaSimulacao[],
  perguntasAtivas: Pergunta[],
  meta: { id: string; data: string },
  seed = 11,
  taxa: { valor: number; origem: 'historico' | 'referencia_nao_calculada' } = {
    valor: TAXA_RESPOSTA_REFERENCIA,
    origem: 'referencia_nao_calculada',
  },
): ResultadoCampanha {
  const n = contatos.length;
  const resp = Math.round(n * taxa.valor);
  const bairros = [...new Set(contatos.map((c) => c.bairro).filter((b): b is string => !!b))];
  const municipioPorBairro: Record<string, string> = {};
  contatos.forEach((c) => {
    if (c.bairro && c.municipio && !municipioPorBairro[c.bairro]) {
      municipioPorBairro[c.bairro] = c.municipio;
    }
  });
  const r = criarGerador(seed);

  const porPergunta: RespostaPergunta[] = perguntasAtivas.map((p) => {
    const opcoes = p.opts || ESCALAS[p.e];
    const dist: Record<string, number[]> = {};
    bairros.forEach((b) => {
      const nb = n ? Math.round((resp * contatos.filter((c) => c.bairro === b).length) / n) : 0;
      const d = opcoes.map(() => r());
      const soma = d.reduce((a, x) => a + x, 0);
      dist[b] = opcoes.map((_, i) => (soma ? Math.round((d[i]! / soma) * nb) : 0));
    });
    const totais = opcoes.map((_, i) => Object.values(dist).reduce((a, x) => a + x[i]!, 0));
    return {
      pergunta: p,
      opcoes,
      distribuicaoPorBairro: dist,
      totais,
      respostas: totais.reduce((a, x) => a + x, 0),
    };
  });

  // Perfis cruzados (níveis) — combina respostas de pares de perguntas likert.
  const lik = porPergunta.filter((x) => x.pergunta.e === 'likert');
  let segmentos: SegmentoCruzado[] = [];
  if (lik.length >= 2) {
    const a = (lik.find((x) => /terror|facç/i.test(x.pergunta.q)) || lik[0])!;
    const b = (lik.find((x) => /forças|fora|extern/i.test(x.pergunta.q) && x !== a) || lik.find((x) => x !== a))!;
    if (a.respostas && b.respostas) {
      const agA = (a.totais[0]! + a.totais[1]!) / a.respostas;
      const agB = (b.totais[0]! + b.totais[1]!) / b.respostas;
      segmentos = [
        {
          nome: `Concorda com "${a.pergunta.q.slice(0, 40)}…" E com "${b.pergunta.q.slice(0, 40)}…"`,
          pct: Math.round(agA * agB * 100),
          leitura: 'alinhamento total: público que aceita a medida e o meio',
        },
        {
          nome: 'Concorda com a 1ª, MAS discorda da 2ª',
          pct: Math.round(agA * (1 - agB) * 100),
          leitura:
            'concorda com o fim, rejeita o meio — é aqui que mora a nuance: apoio condicional',
        },
        {
          nome: 'Discorda da 1ª, concorda com a 2ª',
          pct: Math.round((1 - agA) * agB * 100),
          leitura: 'aceita o meio sem abraçar o diagnóstico — sensível a resultado, não a discurso',
        },
        {
          nome: 'Discorda das duas',
          pct: Math.round((1 - agA) * (1 - agB) * 100),
          leitura: 'oposição consistente — não insistir; ouvir',
        },
      ];
    }
  }

  return {
    id: meta.id,
    convidados: n,
    respostas: resp,
    taxaResposta: n ? Math.round((resp / n) * 100) : 0,
    bairros,
    municipioPorBairro,
    porPergunta,
    segmentos,
    data: meta.data,
    simulado: true,
    taxaUsada: taxa,
  };
}

/** proto:1644 `cpFiltrar` — recorta um resultado para um subconjunto de bairros. */
export function cpFiltrar(r: ResultadoCampanha, bairros: string[]): ResultadoCampanha {
  const porPergunta = r.porPergunta.map((x) => {
    const dist: Record<string, number[]> = {};
    bairros.forEach((b) => {
      if (x.distribuicaoPorBairro[b]) dist[b] = x.distribuicaoPorBairro[b];
    });
    const totais = x.opcoes.map((_, i) => Object.values(dist).reduce((a, v) => a + v[i]!, 0));
    return { ...x, distribuicaoPorBairro: dist, totais, respostas: totais.reduce((a, v) => a + v, 0) };
  });
  const lik = porPergunta.filter((x) => x.pergunta.e === 'likert');
  let segmentos: SegmentoCruzado[] = [];
  if (lik.length >= 2) {
    const a = (lik.find((x) => /terror|facç/i.test(x.pergunta.q)) || lik[0])!;
    const b = (lik.find((x) => /forças|fora|extern/i.test(x.pergunta.q) && x !== a) || lik.find((x) => x !== a))!;
    if (a.respostas && b.respostas) {
      const agA = (a.totais[0]! + a.totais[1]!) / a.respostas;
      const agB = (b.totais[0]! + b.totais[1]!) / b.respostas;
      segmentos = [
        { pct: Math.round(agA * agB * 100) },
        { pct: Math.round(agA * (1 - agB) * 100) },
        { pct: Math.round((1 - agA) * agB * 100) },
        { pct: Math.round((1 - agA) * (1 - agB) * 100) },
      ];
    }
  }
  return {
    ...r,
    bairros,
    porPergunta,
    segmentos,
    respostas: porPergunta[0] ? porPergunta[0].respostas : 0,
  };
}

/**
 * proto:1614 `cpLeitura` — traduzida linha a linha. `tema` e `evitar`
 * substituem as leituras de `STATE.cp.tema`/`PERSONA.evitar` do protótipo.
 */
/**
 * Divergência REAL entre bairros na primeira afirmação de concordância:
 * `max - min` do percentual de concordância (níveis 1+2) calculado sobre a
 * distribuição por bairro que já existe em `RespostaPergunta`.
 *
 * O protótipo não tinha esta frase; a porta anterior escrevia
 * `15 + nBairros*3` p.p., que é função da QUANTIDADE de bairros e não da
 * distribuição — número fabricado sob o rótulo "Cálculo.". Quando não há o
 * que comparar, esta função devolve `null` e a leitura declara a lacuna.
 */
export function divergenciaPorBairro(
  r: ResultadoCampanha,
): { pp: number; maior: { bairro: string; pct: number }; menor: { bairro: string; pct: number } } | null {
  const primeira = r.porPergunta.find((x) => x.pergunta.e === 'likert');
  if (!primeira) return null;
  const porBairro = r.bairros
    .map((b) => {
      const d = primeira.distribuicaoPorBairro[b];
      if (!d) return null;
      const tot = d.reduce((a, v) => a + v, 0);
      if (!tot) return null;
      return { bairro: b, pct: Math.round(((d[0]! + d[1]!) / tot) * 100) };
    })
    .filter((x): x is { bairro: string; pct: number } => x !== null)
    .sort((a, b) => b.pct - a.pct);
  if (porBairro.length < 2) return null;
  const maior = porBairro[0]!;
  const menor = porBairro[porBairro.length - 1]!;
  return { pp: maior.pct - menor.pct, maior, menor };
}

export function cpLeitura(
  r: ResultadoCampanha,
  tema: TemaPerguntas = 'geral',
  evitar: string[] = [],
  nomeBairro: (id: string) => string = (id) => id,
): string {
  const pri = r.porPergunta.find((x) => x.pergunta.e === 'prio');
  const lik = r.porPergunta.filter((x) => x.pergunta.e === 'likert');
  const top = pri ? pri.opcoes.map((o, i) => ({ o, v: pri.totais[i]! })).sort((a, b) => b.v - a.v) : [];
  const cond = r.segmentos[1];

  const listaBairros = r.bairros.map(nomeBairro);
  const rotuloBairros = listaBairros.length > 6
    ? `${listaBairros.length} bairros`
    : listaBairros.join(', ');

  const fato = `<b>Fato.</b> ${r.respostas} respostas (${r.taxaResposta}% de ${r.convidados.toLocaleString('pt-BR')} convidados) em ${r.bairros.length} bairro(s). ${
    top.length && pri
      ? `Maior necessidade: ${top[0]!.o} (${Math.round((top[0]!.v / pri.respostas) * 100)}%), depois ${top[1] ? `${top[1].o} (${Math.round((top[1].v / pri.respostas) * 100)}%).` : ''}`
      : ''
  } ${lik
    .map(
      (x) =>
        `"${x.pergunta.q.slice(0, 45)}…": ${Math.round(((x.totais[0]! + x.totais[1]!) / (x.respostas || 1)) * 100)}% concordam, ${Math.round(((x.totais[3]! + x.totais[4]!) / (x.respostas || 1)) * 100)}% discordam`,
    )
    .join('; ')}.`;

  const calculo = `<b>Cálculo.</b> ${
    cond
      ? `Cruzando as afirmações-chave: ${r.segmentos[0]!.pct}% concordam com as duas; <b>${cond.pct}% concordam com a primeira mas discordam da segunda</b> — apoio condicional; ${r.segmentos[3]!.pct}% discordam de ambas.`
      : 'Perguntas de escala insuficientes para cruzamento (use ao menos 2 afirmações de concordância).'
  } ${(() => {
    const div = divergenciaPorBairro(r);
    if (div) {
      return `Variação por bairro: ${rotuloBairros} divergem em até <b>${div.pp} p.p.</b> na concordância com a primeira afirmação (${nomeBairro(div.maior.bairro)} ${div.maior.pct}% × ${nomeBairro(div.menor.bairro)} ${div.menor.pct}%).`;
    }
    if (!r.porPergunta.some((x) => x.pergunta.e === 'likert')) {
      return 'Variação por bairro: não calculada — nenhuma afirmação de concordância neste questionário.';
    }
    return `Variação por bairro: não calculada — ${
      r.bairros.length ? rotuloBairros : 'nenhum bairro'
    } com respostas; são necessários ao menos 2 bairros com respostas para comparar.`;
  })()}`;

  const interpretacao = `<b>Interpretação.</b> ${
    cond && cond.pct > 20
      ? `A população não está em "sim ou não": um em cada ${Math.max(2, Math.round(100 / cond.pct))} aceita o objetivo mas rejeita o meio. A estratégia deve falar do <i>resultado</i> (${
          tema === 'seguranca' ? '"bairro seguro, com a nossa gente"' : '"o problema resolvido"'
        }) e evitar o <i>meio</i> que divide. ${evitar.length ? `Coerente com o que você evita (${evitar.join(', ')}).` : ''}`
      : 'Opinião relativamente homogênea — dá para falar direto do tema.'
  } ${top.length ? `Em ações, priorize ${top[0]!.o.toLowerCase()}.` : ''}`;

  const fonte = `<small>Fonte: campanha ${r.id} · ${r.data} · confiança ${
    r.respostas > 150 ? 'média-alta' : 'média'
  } (amostra autosselecionada, base própria${
    r.simulado
      ? `, distribuição de respostas SIMULADA — o webhook de respostas do WhatsApp ainda não alimentou esta campanha; o volume de respostas veio de ${
          r.taxaUsada.origem === 'historico'
            ? `${Math.round(r.taxaUsada.valor * 100)}% medido nas campanhas anteriores do mandato`
            : `${Math.round(r.taxaUsada.valor * 100)}% de referência, não calculado sobre campanha alguma`
        }`
      : ''
  }) · registrada em Fontes &amp; conhecimento e no Perfil do território</small>`;

  return `${fato}<br>\n ${calculo}<br>\n ${interpretacao}<br>\n ${fonte}`;
}

/**
 * Recorte pedido em linguagem natural (proto:1645, bloco `bs`/`munMatch`/`rec`).
 *
 * Reconhece o nome de um bairro coberto pela campanha ("só quem é de
 * Jangurussu") ou o município de um dos bairros ("só o município de
 * Fortaleza") dentro do texto da pergunta. Devolve o subconjunto já filtrado
 * por `cpFiltrar` e o rótulo do recorte, que TODAS as respostas citam — sem
 * isso o motor devolvia o agregado de todos os bairros sem avisar que tinha
 * ignorado o recorte pedido.
 */
export function detectarRecorte(
  r: ResultadoCampanha,
  pergunta: string,
  nomeBairro: (id: string) => string = (id) => id,
): { sub: ResultadoCampanha; rec: string; recortado: boolean } {
  const t = pergunta.toLowerCase();
  const bs = r.bairros.filter((b) => {
    const nome = nomeBairro(b).toLowerCase();
    return nome.length > 2 && t.includes(nome);
  });
  if (bs.length) {
    return { sub: cpFiltrar(r, bs), rec: bs.map(nomeBairro).join(' + '), recortado: true };
  }
  const municipios = [...new Set(Object.values(r.municipioPorBairro))];
  const mun = municipios.find((m) => m.length > 2 && t.includes(m.toLowerCase()));
  if (mun) {
    const ids = r.bairros.filter((b) => r.municipioPorBairro[b] === mun);
    return { sub: cpFiltrar(r, ids), rec: `município de ${mun}`, recortado: true };
  }
  return { sub: r, rec: 'todos os respondentes', recortado: false };
}

/** proto:1645 `cpResponder` — motor de respostas do chat "Conversar sobre a campanha". */
export function cpResponder(
  r: ResultadoCampanha,
  pergunta: string,
  nomeBairro: (id: string) => string = (id) => id,
): string {
  const t = pergunta.toLowerCase();
  const { sub, rec, recortado } = detectarRecorte(r, pergunta, nomeBairro);

  if (recortado && !sub.respostas) {
    return `<b>Sem respostas</b> nesse recorte (${rec}). A campanha ${r.id} cobriu ${r.bairros
      .map(nomeBairro)
      .join(', ')}.<br><small>Fonte: campanha ${r.id}</small>`;
  }

  const pq = sub.porPergunta.find((x) =>
    t
      .split(' ')
      .filter((w) => w.length > 4)
      .some((w) => x.pergunta.q.toLowerCase().includes(w)),
  );

  if (/quantos|quantas|total|responderam|enviad/.test(t) && !pq) {
    return `<b>Fato.</b> Campanha ${r.id}: ${r.convidados.toLocaleString('pt-BR')} enviadas, ${r.respostas} respondidas (${r.taxaResposta}%) em ${r.data}. No recorte <i>${rec}</i>: ${sub.respostas} respostas.<br><b>Cálculo.</b> ${
      sub.porPergunta[0]
        ? sub.bairros
            .map(
              (b) =>
                `${nomeBairro(b)} ${(sub.porPergunta[0]!.distribuicaoPorBairro[b] || []).reduce((a, v) => a + v, 0)}`,
            )
            .join(' · ')
        : '—'
    }.<br><small>Fonte: campanha ${r.id} · recorte ${rec}</small>`;
  }

  if (pq) {
    const tot = pq.respostas || 1;
    return `<b>Fato.</b> "${pq.pergunta.q}" · recorte <i>${rec}</i> · ${tot} respostas: ${pq.opcoes
      .map((o, i) => `${o} ${Math.round((pq.totais[i]! / tot) * 100)}%`)
      .join(' · ')}.<br><b>Cálculo.</b> ${
      pq.pergunta.e === 'likert'
        ? `Concordância líquida: ${Math.round(((pq.totais[0]! + pq.totais[1]! - pq.totais[3]! - pq.totais[4]!) / tot) * 100)} p.p.`
        : `Opção dominante: ${pq.opcoes[pq.totais.indexOf(Math.max(...pq.totais))]!}.`
    }<br><small>Fonte: campanha ${r.id} · recorte ${rec}</small>`;
  }

  if (/compar|diferen|versus|vs/.test(t) && r.bairros.length > 1) {
    const por = r.bairros.map((b) => {
      const s = cpFiltrar(r, [b]);
      const p = s.porPergunta.find((x) => x.pergunta.e === 'prio');
      const topLocal = p ? p.opcoes[p.totais.indexOf(Math.max(...p.totais))]! : '—';
      return { b, seg: s.segmentos[1] ? s.segmentos[1].pct : null, top: topLocal };
    });
    return `<b>Comparativo por bairro · ${r.id}</b><br>${por
      .map((x) => `• <b>${nomeBairro(x.b)}</b>: maior necessidade ${x.top}${x.seg !== null ? ` · apoio condicional ${x.seg}%` : ''}`)
      .join('<br>')}<br><small>Fonte: campanha ${r.id}</small>`;
  }

  const pri = sub.porPergunta.find((x) => x.pergunta.e === 'prio');
  const lik = sub.porPergunta.filter((x) => x.pergunta.e === 'likert');
  const top = pri ? pri.opcoes.map((o, i) => ({ o, v: pri.totais[i]! })).sort((a, b) => b.v - a.v) : [];
  return `<b>Fato.</b> Recorte <i>${rec}</i> · ${sub.respostas} respostas. ${
    top.length && pri ? `Maior necessidade: ${top[0]!.o} (${Math.round((top[0]!.v / pri.respostas) * 100)}%).` : ''
  } ${lik
    .slice(0, 2)
    .map((x) => `"${x.pergunta.q.slice(0, 40)}…": ${Math.round(((x.totais[0]! + x.totais[1]!) / (x.respostas || 1)) * 100)}% concordam`)
    .join('; ')}.<br><b>Cálculo.</b> ${
    sub.segmentos[1]
      ? `Apoio condicional (concorda com o fim, discorda do meio): ${sub.segmentos[1]!.pct}%; oposição consistente: ${sub.segmentos[3]!.pct}%.`
      : 'Sem cruzamento possível neste recorte.'
  }<br><small>Fonte: campanha ${r.id} · ${r.data} · recorte ${rec}</small>`;
}

/** proto:1657 `cpPerguntar` — reconhece a pergunta "como você chegou". */
export function ehPerguntaComoCheguei(pergunta: string): boolean {
  return /como (você )?chegou|como calcul/.test(pergunta.toLowerCase());
}

/** proto:1657 — resposta canônica de "Como cheguei", incluindo a citação da fonte. */
export function respostaComoCheguei(r: ResultadoCampanha): string {
  const cond = r.segmentos[1];
  return `<b>Como cheguei.</b> 1) Cada resposta de concordância vale um nível (totalmente → em parte → neutro → em parte → totalmente), não um sim/não. 2) Cruzei as duas afirmações-chave da campanha por respondente: quem concorda que facções são terroristas <i>e</i> discorda de forças externas no bairro forma o grupo "apoio condicional" (${
    cond ? `${cond.pct}%` : '—'
  }). 3) Fiz o mesmo por bairro e comparei com as solicitações e as mídias do território. 4) A interpretação é apoio à decisão — a amostra é autosselecionada (só opt-in que respondeu).<br><small>Fonte: campanha ${r.id} · método registrado em auditoria</small>`;
}
